import axios, { AxiosStatusCode } from 'smg/axios';
import * as buttons from 'smg/buttons';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import { InitGoogleCredentialHandler } from 'smg/googlesignin';
import Modal from 'smg/modal';
import * as object from 'smg/object';
import { Phone } from 'smg/phone';
import { Relref } from '../hugo';

export interface PromptLoginCodeOptions {
    phone?: Phone;
    email?: string;
}

export declare interface LoginResp {
    token?: string;
    emails?: string[];
}

export async function PromptLoginCode(opts: PromptLoginCodeOptions) {
    const tel = await opts.phone?.Format();

    const createCode = axios.post('/sessions/code', {
        'phone': tel,
        'email': opts.email,
    });

    const login = createCode.then(async () => {
        const body = (document.querySelector('#modal-login-code') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;

        const phone = body.querySelector('#modal-phone') as HTMLElement;
        phone.textContent = await opts.phone?.Human() || opts.email || '';

        const code = body.querySelector('#modal-code') as HTMLInputElement;

        const resend = body.querySelector('#modal-resend') as HTMLAnchorElement;
        const resendSpinner = body.querySelector('.spinner-border') as HTMLElement;
        return new Promise<void>(resolve => {
            resend.addEventListener('click', e => {
                e.preventDefault();

                resend.setAttribute('disabled', 'disabled');
                resendSpinner.classList.remove('d-none');
                m.HideSuccess();

                void axios.post('/sessions/code', {
                    'phone': tel,
                    'email': opts.email,
                }).then(() => {
                    m.ShowSuccess('Your code has been sent!');
                }).finally(() => {
                    resend.removeAttribute('disabled');
                    resendSpinner.classList.add('d-none');
                });
            });

            const m = new Modal({
                body,
                submitAutoFocus: false,
                onSubmit: async () => axios.post<LoginResp>('/sessions', {
                    'phone': tel,
                    'email': opts.email,
                    'code': code.value,
                }).then(resp => {
                    if (resp.status === 202 && resp.data.emails?.length) {
                        const body = (document.querySelector('#modal-login-select-email') as HTMLTemplateElement).content.cloneNode(true) as DocumentFragment;

                        const listGroup = body.querySelector('.list-group') as HTMLElement;
                        const listGroupItem = listGroup.querySelector('.list-group-item') as HTMLAnchorElement;

                        listGroup.removeChild(listGroupItem);

                        resp.data.emails.sort();
                        for (const email of resp.data.emails) {
                            const a = listGroupItem.cloneNode(true) as HTMLAnchorElement;
                            a.textContent = email;
                            listGroup.appendChild(a);

                            a.addEventListener('click', e => {
                                e.preventDefault();

                                axios.post<LoginResp>('/sessions', {
                                    'phone': tel,
                                    'email': email,
                                    'code': code.value,
                                }).then(resp => {
                                    if (resp.data.token) {
                                        session.SetLoginToken(resp.data.token);
                                        resolve();
                                        return;
                                    }

                                    throw Error('Login failed!');
                                }).catch(err => {
                                    switch (AxiosStatusCode(err)) {
                                    case 404:
                                        throw new Error('Code is invalid or expired.');
                                    }

                                    throw err;
                                });
                            });
                        }

                        new Modal({ body });

                        return;
                    }

                    if (resp.data.token) {
                        session.SetLoginToken(resp.data.token);
                        resolve();
                        return;
                    }

                    throw Error('Login failed!');
                }).catch(err => {
                    switch (AxiosStatusCode(err)) {
                    case 404:
                        throw new Error('Code is invalid or expired.');
                    }

                    throw err;
                }),
            });

            // TODO: if modal animation is turned on this doesn't work
            // probably just need to timeout until the animation is done
            setTimeout(() => code.focus());
        });
    });

    return { createCode, login };
}

export default function main() {
    InitGoogleCredentialHandler();

    const phone = document.querySelector('#phone') as HTMLInputElement;
    setTimeout(() => phone.focus());

    const form = document.querySelector('#login-form') as HTMLFormElement;
    const fieldset = form.querySelector('fieldset') as HTMLFieldSetElement;
    const confirm = form.querySelector('[type="submit"]') as HTMLButtonElement;
    const alert = form.querySelector('.alert') as HTMLElement;
    const heading = form.querySelector('h1') as HTMLHeadElement;

    alert.parentElement?.removeChild(alert);
    alert.classList.remove('d-none');

    const showAlert = (type: 'generic' | 'invalid-email' | 'email-unverified') => {
        const a = alert.cloneNode(true) as HTMLElement;
        const msg = a.querySelector(`.${type}`);
        msg?.classList.remove('d-none');

        heading.after(a);
    };

    const hideAlert = () => {
        const a = form.querySelector('.alert');
        a?.parentElement?.removeChild(a);
    };

    const p = new Phone(phone);

    form?.addEventListener('submit', async e => {
        e.preventDefault();

        hideAlert();

        fieldset.setAttribute('disabled', 'disabled');
        buttons.Start(confirm);

        const prompt = await PromptLoginCode({ phone: p });

        void prompt.createCode.finally(() => {
            fieldset.removeAttribute('disabled');
            buttons.Stop(confirm);
        });

        prompt.login.
            then(() => window.location.href = Relref('/dashboard')).
            catch(err => {
                if (errors.IsAxiosError(err) && errors.IsBindErrors(err.response?.data) && object.IsObject(err['response']) && 'phone' in err.response.data) {
                    errors.HandleRequestError(err);
                } else {
                    showAlert('generic');
                }

                throw err;
            });
    });
}